import { match } from "ts-pattern";

import { SwapProductDiscountQuery } from "shared/SwapActiveOrder/swap-product-discount.query.generated";
import { useSwapActiveOrder } from "shared/SwapActiveOrder/useSwapActiveOrder";
import { formatToCurrency } from "utils/price.utils";

// Note: This code is related to the implementation of
// getUpfrontDiscountPercentage in api-v2/src/modules/order/swap/order-swap.service.ts:481.
export const getSeniorityThreshold = ({
  commitmentPeriod,
}: {
  commitmentPeriod: number | undefined;
}) =>
  match(commitmentPeriod || 0)
    .with(0, 1, () => 10)
    .with(12, 24, (period) => period - 2)
    .otherwise((period) => period);

export type UsePriceManagementProps = {
  originalUpfrontPrice: number | null;
  upfrontPrice: number;
  originalSubscriptionPrice: number | null;
  subscriptionPrice: number;
  parentOrderId?: number;
};

export interface PriceAdvantage {
  discountInfo: SwapProductDiscountQuery | undefined;
  hasUpfrontPriceDrop: boolean | 0 | null | undefined;
  smartphoneFamilyHasSubscriptionPriceDrop: boolean | 0 | null | undefined;
  smartphoneFamilyHasUpfrontPriceDrop: boolean | 0 | null | undefined;
  hasAdvantage: boolean | 0 | null | undefined;
  formatedOriginalPriceSubscription: string;
  formatedOriginalPriceUpfront: string;
  discountPercentage: number | undefined;
  seniority: number;
  currentSeniorityPlan: number | false;
  hasSeniorityDiscount: boolean;
}

export const usePriceAdvantage = ({
  originalUpfrontPrice,
  originalSubscriptionPrice,
  subscriptionPrice,
  upfrontPrice,
  parentOrderId,
}: Partial<UsePriceManagementProps>): PriceAdvantage => {
  const { discountInfo } = useSwapActiveOrder({ parentOrderId });

  const discountPercentage = discountInfo?.discountPercentage?.discount;

  // client has discount for his seniority
  const hasDiscount = !!discountPercentage;

  // prices have gone down (e.g., BlackFriday)
  const hasUpfrontPriceDrop =
    originalUpfrontPrice &&
    upfrontPrice !== originalUpfrontPrice &&
    upfrontPrice !== 0;

  // the smartphone family has a formerSubscriptionPriceInCentsIncludingTax which is less than the new price
  const smartphoneFamilyHasSubscriptionPriceDrop =
    originalSubscriptionPrice &&
    (subscriptionPrice as number) < originalSubscriptionPrice;

  const smartphoneFamilyHasUpfrontPriceDrop =
    originalUpfrontPrice && (upfrontPrice as number) < originalUpfrontPrice;

  const originalPriceUpfront = originalUpfrontPrice || upfrontPrice;

  const originalPriceSubscription =
    originalSubscriptionPrice || subscriptionPrice;

  const formatedOriginalPriceUpfront = formatToCurrency(
    "fr-FR",
    "EUR",
    originalPriceUpfront ?? 0,
    undefined,
    true,
  );

  const formatedOriginalPriceSubscription = formatToCurrency(
    "fr-FR",
    "EUR",
    originalPriceSubscription ?? 0,
    undefined,
    false,
  );

  const hasAdvantage =
    hasDiscount ||
    hasUpfrontPriceDrop ||
    smartphoneFamilyHasSubscriptionPriceDrop;

  const seniority = discountInfo?.orderCommitmentInfo.seniority || 0;
  const seniorityThreshold = getSeniorityThreshold({
    commitmentPeriod: discountInfo?.orderCommitmentInfo.commitmentPeriod,
  });

  const hasSeniority = !!seniorityThreshold && !!seniority;
  const hasSeniorityDiscount = hasSeniority && seniority >= seniorityThreshold;
  const currentSeniorityPlan = hasSeniorityDiscount && seniorityThreshold;

  return {
    discountInfo,
    hasUpfrontPriceDrop,
    smartphoneFamilyHasSubscriptionPriceDrop,
    smartphoneFamilyHasUpfrontPriceDrop,
    hasAdvantage,
    formatedOriginalPriceSubscription,
    formatedOriginalPriceUpfront,
    discountPercentage,
    seniority,
    currentSeniorityPlan,
    hasSeniorityDiscount,
  };
};
